import { Analysis, PossibleRootCauseCategory } from '.';

/**
 * Thyroid 5-Area Physiology pattern detection.
 *
 * Two layers live here:
 *  1. The class `calculateInsights()` pushes the per-marker 14xxx Possible Root
 *     Cause categories. These are the categoryIds whose root-cause content has
 *     been uploaded to DynamoDB, so they are what makes the "Possible Root
 *     Causes" display populate today.
 *  2. The pure, exported `resolveThyroidPattern` implements the TSH-first
 *     decision tree from MaryAnn's decks (pp. 48–50) → 80xxx pattern
 *     categoryIds. It is unit-tested directly (ThyroidAnalysis.test.ts) and
 *     drives the dev-only 5-Area Map (thyroid-regulation-insights.tsx).
 *
 * The 80xxx pattern categories are NOT yet uploaded to DynamoDB, so the class
 * intentionally does NOT push them — doing so would leave the Possible Root
 * Causes empty. Once the 80xxx considerations are uploaded, switch
 * `calculateInsights()` over to `resolveThyroidPattern`.
 *
 * See thyroid-patterns-spec.md (§5 patterns, §6 refinements) and
 * thyroid-patterns-validation.md.
 */

/** Per-marker state. 'absent' = not measured. */
export type Tri = 'low' | 'opt' | 'high' | 'absent';

export interface ThyroidMarkerStates {
  // Core triad
  tsh: Tri;
  ft4: Tri;
  ft3: Tri;
  // Add-ons (refine the area once the triad picks the branch)
  rt3: Tri;
  tt4: Tri;
  tt3: Tri;
  tbg: Tri;
  t3u: Tri;
  /** TPOAb and/or TgAb above optimal. */
  abPos: boolean;
}

/**
 * Resolve the marker states to a single thyroid pattern categoryId (80xxx), or
 * null if nothing fires. Rules are evaluated TSH-branch first; within a branch,
 * add-on-specific leaves precede triad defaults.
 *
 * Triad-first gating: the Low-TSH and High-TSH branches resolve on the triad
 * alone (add-ons only refine). The Optimal-TSH branch is the Transport/
 * Utilization story and legitimately *needs* add-ons (TT4/TT3, TBG/T3U, rT3) —
 * with the triad alone it returns null and the 5-Area Map shows "add markers".
 */
export function resolveThyroidPattern(s: ThyroidMarkerStates): number | null {
  const { tsh, ft4, ft3, rt3, tt4, tt3, tbg, t3u, abPos } = s;
  const low = (x: Tri) => x === 'low';
  const opt = (x: Tri) => x === 'opt';
  const high = (x: Tri) => x === 'high';
  const present = (x: Tri) => x !== 'absent';

  // Nothing to assess, or no TSH to pick a branch.
  if (!present(tsh) && !present(ft4) && !present(ft3)) return null;
  if (!present(tsh)) return null;

  // ───────────────────────── LOW TSH ─────────────────────────
  if (low(tsh)) {
    // High free hormones → overproduction (autoimmune hashitoxicosis vs non-AI/Graves)
    if (high(ft4) && high(ft3)) return abPos ? 80100 : 80130;
    // Euthyroid free + antibodies → early autoimmune (euthyroid phase)
    if (opt(ft4) && opt(ft3) && abPos) return 80100;
    // Adequate T4 but low active T3 with an rT3 shunt → under-conversion
    if ((high(ft4) || opt(ft4)) && low(ft3) && high(rt3)) return 80300;
    // Low free hormones → signaling (optimal rT3) vs prolonged downregulation (high rT3)
    if (low(ft4) && low(ft3)) return high(rt3) ? 80410 : 80000;
    return null;
  }

  // ───────────────────────── HIGH TSH ─────────────────────────
  if (high(tsh)) {
    // High free + non-suppressed TSH → inappropriate TSH (SITSH; TSHoma/RTHβ)
    if (high(ft4) && high(ft3)) return 80010;
    // High active T3 despite high TSH → over-conversion to T3, or impaired activation
    if (high(ft3)) {
      if (opt(ft4) && !low(rt3)) return 80440; // opt FT4 + high FT3 + opt/high rT3
      return 80320; // over-conversion to T3 (low FT4 and/or low rT3)
    }
    // Low active T3 → late autoimmune / over-conversion to rT3 / underproduction
    if (low(ft3)) {
      if (abPos) return 80110; // late autoimmune
      if (high(rt3)) return 80310; // over-conversion to rT3
      return 80120; // underproduction (non-AI; consider blocking-AI on follow-up TRAb)
    }
    return null; // FT3 optimal with high TSH = subclinical hypo — no distinct pattern
  }

  // ───────────────────────── OPTIMAL TSH ─────────────────────────
  // Transport / Utilization story — needs add-ons to resolve.
  if (opt(tsh)) {
    // High active T3 → utilization: receptor-α (low rT3) vs early poor uptake (opt/high rT3)
    if (high(ft3) && (low(ft4) || opt(ft4))) {
      if (low(rt3)) return 80430; // RTHα — low rT3 + low T4/T3 ratio
      if (high(rt3) || opt(rt3)) return 80400; // early cell-membrane uptake dysfunction
      return null; // need rT3 to split α vs uptake
    }
    // Low active T3 + high rT3 → under-conversion (T4 adequate) vs NTIS (T4 low).
    // The 80300/80450 split here is a known overlap (validation §refinements).
    if (low(ft3) && high(rt3)) {
      return low(ft4) ? 80450 : 80300;
    }
    // Low free → short-term high TBG (high totals + binding) or NTIS (high rT3)
    if (low(ft4) && low(ft3)) {
      if (high(tt4) && high(tt3) && (high(tbg) || low(t3u))) return 80200;
      if (high(rt3)) return 80450;
      return null;
    }
    // High free → inappropriate TSH (high totals) or short-term low TBG (low totals)
    if (high(ft4) && high(ft3)) {
      if (high(tt4) && high(tt3)) return 80010;
      if (low(tt4) && low(tt3) && (low(tbg) || high(t3u))) return 80220;
      return null;
    }
    // Euthyroid free → compensated transport / altered binding proteins / NTIS
    if (opt(ft4) && opt(ft3)) {
      if (high(tt4) && high(tt3) && (high(tbg) || low(t3u))) return 80210; // long-term high TBG
      if (low(tt4) && low(tt3) && (low(tbg) || high(t3u))) return 80230; // long-term low TBG
      if (high(tt4) && opt(tbg) && opt(t3u)) return 80240; // high albumin / TTR
      if (high(rt3)) return 80450; // NTIS
      return null;
    }
    return null;
  }

  return null;
}

export class ThyroidAnalysis extends Analysis {
  /**
   * Possible Root Causes for the Thyroid section come from DynamoDB, keyed by
   * categoryId. The per-marker 14xxx categories below are the ones that have been
   * uploaded, so this pushes those (restoring the working "Possible Root Causes"
   * display). The 80xxx pattern categories from `resolveThyroidPattern` are NOT
   * yet in the DB, so the class does not push them here — that switch happens once
   * the 80xxx considerations are uploaded. `resolveThyroidPattern` is still
   * exported and drives the dev-only 5-Area Map (thyroid-regulation-insights.tsx).
   */
  calculateInsights() {
    if (this.ba.tsh.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14000);
    } else if (this.ba.tsh.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14010);
    }

    if (this.ba.t4Total.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14020);
    } else if (this.ba.t4Total.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14030);
    }

    if (this.ba.t4Free.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14040);
    } else if (this.ba.t4Free.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14050);
    }

    if (this.ba.t3Total.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14060);
    } else if (this.ba.t3Total.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14070);
    }

    if (this.ba.t3Free.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14072);
    } else if (this.ba.t3Free.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14074);
    }

    if (this.ba.reverseT3.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14080);
    } else if (this.ba.reverseT3.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14090);
    }

    if (this.ba.tpoAb.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14100);
    }

    if (this.ba.tgAb.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14120);
    }

    if (this.ba.tbg.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14140);
    } else if (this.ba.tbg.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14150);
    }

    if (this.ba.t3Uptake.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14160);
    } else if (this.ba.t3Uptake.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14170);
    }

    if (this.ba.freeT4Index.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14180);
    } else if (this.ba.freeT4Index.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_14190);
    }
  }
}
